export type SafeAreaInsets = { top: number; right: number; bottom: number; left: number };

const ZERO: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * Reads the device safe-area insets (iOS notch / Dynamic Island / home
 * indicator) in CSS px, for JS-positioned overlays that must stay clear of them.
 *
 * `env(safe-area-inset-*)` is only available in CSS, so this measures it through
 * a throwaway probe element padded by the four --safe-* tokens rather than by
 * env() directly, so it picks up the insets the Android shell injects as
 * --sa-top/right/bottom/left (WebView's own env(safe-area-inset-*) is
 * cutout-only there and misses the system bars entirely — measured on an API 36
 * device: env() top 0px / bottom 0px against a real 54px status bar and 24px
 * gesture bar). The probe inherits the custom properties from <html>. The native
 * shell
 * paints edge-to-edge (`viewport-fit=cover`, capacitor `contentInset: 'never'`),
 * so the insets are real there; on web / non-notch devices every value is 0.
 * Cheap (one layout read), so callers can measure on demand rather than cache —
 * the values change on rotation.
 */
export function getSafeAreaInsets(): SafeAreaInsets {
  if (typeof document === 'undefined' || !document.body) {
    return ZERO;
  }
  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText =
    'position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
    'padding-top:var(--safe-top);padding-right:var(--safe-right);' +
    'padding-bottom:var(--safe-bottom);padding-left:var(--safe-left);';
  document.body.appendChild(probe);
  try {
    const cs = getComputedStyle(probe);
    return {
      top: Number.parseFloat(cs.paddingTop) || 0,
      right: Number.parseFloat(cs.paddingRight) || 0,
      bottom: Number.parseFloat(cs.paddingBottom) || 0,
      left: Number.parseFloat(cs.paddingLeft) || 0,
    };
  } finally {
    probe.remove();
  }
}
